import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { apiClient } from '@/api/client';
import { useAuth } from '@/auth/AuthContext';

const ScopeContext = createContext(null);

export function ScopeProvider({ children }) {
    const { user } = useAuth();
    const [brands, setBrands] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [brandId, setBrandId] = useState(null);
    const [accountId, setAccountId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [globalEmergencyStop, setGlobalEmergencyStop] = useState(false);

    const refresh = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        try {
            const [b, a, g] = await Promise.all([
                apiClient.get('/brands'),
                apiClient.get('/accounts'),
                apiClient.get('/automation/global'),
            ]);
            setBrands(b.data);
            setAccounts(a.data);
            setGlobalEmergencyStop(Boolean(g.data?.global_emergency_stop));
        } catch (e) {
            console.error('scope refresh failed', e);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        if (user) refresh();
    }, [user, refresh]);

    const accountsForBrand = useMemo(() => {
        if (!brandId) return accounts;
        return accounts.filter((a) => a.brand_id === brandId);
    }, [accounts, brandId]);

    const value = useMemo(
        () => ({
            brands,
            accounts,
            accountsForBrand,
            brandId,
            setBrandId: (id) => {
                setBrandId(id);
                setAccountId(null);
            },
            accountId,
            setAccountId,
            refresh,
            loading,
            globalEmergencyStop,
            setGlobalEmergencyStop,
        }),
        [brands, accounts, accountsForBrand, brandId, accountId, refresh, loading, globalEmergencyStop],
    );

    return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

export function useScope() {
    const ctx = useContext(ScopeContext);
    if (!ctx) throw new Error('useScope must be used inside ScopeProvider');
    return ctx;
}

export function brandColorVar(slot) {
    const s = Math.max(1, Math.min(5, slot || 1));
    return `var(--brand-accent-${s})`;
}
